/**
 * Zone tool (Z / toolbar Zone): draw a closed ZONE — drag for a freehand shape, or click points and
 * Enter / double-click to close it — then name it in the banner. Fixtures inside the zone get that
 * ZONE in the AREA/ZONE BOM (backend/apps/takeoff/zones.py, mirrored in web/shared/src/zones.ts).
 *
 * The overlay captures the pointer the way the calibration and mirror tools do, so Excalidraw doesn't
 * select or move anything while a zone is being drawn. While a zone is open (a point drawn, or the naming
 * step) the tool is modal: keys go through BoardPage's `zoneKey` (the keymap's intercept), other tools and
 * placements through `zoneInterrupt` — with 3+ points they close the shape and ask for its name first (the
 * other tool starts once it's named), with fewer the zone is dropped. Drafts are local: nothing is in the
 * scene (or seen by collaborators) until the name is confirmed, so a reload never leaves half a zone.
 */
import type { ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types';
import { useEffect, useRef, useState } from 'react';
import { simplify } from '../../shared/src/zones';

type Pt = [number, number];

export interface ZoneDraft {
  /** Committed outline points (scene). */
  pts: Pt[];
  /** True while dragging a freehand outline. */
  dragging: boolean;
  /** Closed and waiting for its name. */
  naming: boolean;
  defaultName: string;
  /** Bumped when something was blocked while the zone is open: the banner says how to finish. */
  nudge?: number;
}

/** Translucent fills for zones, cycled per zone. */
export const ZONE_COLOURS = ['#1971c2', '#2f9e44', '#e8590c', '#9c36b5', '#0c8599', '#c2255c', '#5f3dc4', '#e67700'];

/** Shown for a moment when a click / key / tool was paused because a zone is open. */
export function zoneBlocked(d: ZoneDraft): string {
  return d.naming ? `Finish the zone (Enter) — Esc keeps it as ${d.defaultName}` : 'Finish the zone (Enter) or Esc to cancel';
}

export function zoneBanner(d: ZoneDraft): string {
  if (d.naming) return `Name the zone · Enter adds it · Esc keeps it as ${d.defaultName}`;
  if (!d.pts.length) return 'Zone · drag to draw a zone · or click points · Enter / double-click to close · Esc cancels';
  if (d.dragging) return 'Zone · release to close the shape';
  return `Zone · ${d.pts.length} point${d.pts.length === 1 ? '' : 's'} · click to add · Enter / double-click to close · Backspace removes the last · Esc cancels`;
}

const DRAG_PX = 6; // screen px of movement before a press becomes a freehand drag

export function ZoneOverlay(p: {
  xapi: ExcalidrawImperativeAPI;
  tick: number;
  draft: ZoneDraft;
  /** Hidden zones, drawn faintly while the tool is on so a new zone doesn't overlap one by accident (no scene change). */
  ghosts?: readonly (readonly Pt[])[];
  onChange(d: ZoneDraft): void;
  onCommit(pts: Pt[], name: string): void;
  onCancel(): void;
}) {
  const { xapi, draft } = p;
  void p.tick;
  const st = xapi.getAppState();
  const z = st.zoom.value;
  const vp = (q: Pt): Pt => [(q[0] + st.scrollX) * z, (q[1] + st.scrollY) * z];
  const ref = useRef(p);
  ref.current = p;
  const [hover, setHover] = useState<Pt | null>(null);
  const [name, setName] = useState(draft.defaultName);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    if (!draft.nudge) return;
    setBlocked(true);
    const t = setTimeout(() => setBlocked(false), 3000);
    return () => clearTimeout(t);
  }, [draft.nudge]);
  useEffect(() => {
    if (draft.naming) {
      setName(draft.defaultName);
      setTimeout(() => nameRef.current?.select(), 0);
    }
  }, [draft.naming, draft.defaultName]);

  useEffect(() => {
    const host = document.querySelector('.xb-canvas .excalidraw') as HTMLElement | null;
    if (!host) return;
    const toScene = (e: PointerEvent | MouseEvent): Pt => {
      const a = xapi.getAppState();
      const r = host.getBoundingClientRect();
      const zz = a.zoom.value;
      return [(e.clientX - r.left) / zz - a.scrollX, (e.clientY - r.top) / zz - a.scrollY];
    };
    let press: { x: number; y: number; start: Pt; freehand: Pt[] | null } | null = null;
    const close = () => {
      const d = ref.current.draft;
      if (d.pts.length >= 3) ref.current.onChange({ ...d, dragging: false, naming: true });
    };
    const down = (ev: Event) => {
      const e = ev as PointerEvent;
      const t = e.target as HTMLElement | null;
      if (!t || t.tagName !== 'CANVAS') return;
      if (ref.current.draft.naming) {
        // Nothing on the board responds until the zone is named.
        e.preventDefault();
        e.stopPropagation();
        const d = ref.current.draft;
        ref.current.onChange({ ...d, nudge: (d.nudge ?? 0) + 1 });
        return;
      }
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      press = { x: e.clientX, y: e.clientY, start: toScene(e), freehand: null };
    };
    const move = (ev: Event) => {
      const e = ev as PointerEvent;
      const pt = toScene(e);
      setHover(pt);
      if (!press) return;
      if (!press.freehand && Math.hypot(e.clientX - press.x, e.clientY - press.y) > DRAG_PX && !ref.current.draft.pts.length) {
        press.freehand = [press.start];
        ref.current.onChange({ ...ref.current.draft, dragging: true, pts: [press.start] });
      }
      if (press.freehand) {
        press.freehand.push(pt);
        ref.current.onChange({ ...ref.current.draft, dragging: true, pts: [...press.freehand] });
      }
    };
    const up = (ev: Event) => {
      const e = ev as PointerEvent;
      if (!press) return;
      e.preventDefault();
      e.stopPropagation();
      const pr = press;
      press = null;
      if (pr.freehand) {
        const tol = 1.5 / xapi.getAppState().zoom.value; // ~1.5 screen px
        const pts = simplify(pr.freehand, tol);
        if (pts.length >= 3) ref.current.onChange({ ...ref.current.draft, pts, dragging: false, naming: true });
        else ref.current.onChange({ ...ref.current.draft, pts: [], dragging: false });
        return;
      }
      const d = ref.current.draft;
      // A click near the first point closes the shape, like Excalidraw's own polygon tool.
      if (d.pts.length >= 3) {
        const [fx, fy] = d.pts[0];
        const zz = xapi.getAppState().zoom.value;
        if (Math.hypot((pr.start[0] - fx) * zz, (pr.start[1] - fy) * zz) < 8) return close();
      }
      ref.current.onChange({ ...d, pts: [...d.pts, pr.start] });
    };
    const dbl = (ev: Event) => {
      if (ref.current.draft.naming) return;
      ev.preventDefault();
      ev.stopPropagation();
      // The double-click's two clicks added one extra point at the same spot: drop it, then close.
      const d = ref.current.draft;
      const pts = d.pts.length > 3 ? d.pts.slice(0, -1) : d.pts;
      if (pts.length >= 3) ref.current.onChange({ ...d, pts, dragging: false, naming: true });
    };
    host.addEventListener('pointerdown', down, true);
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
    host.addEventListener('dblclick', dbl, true);
    return () => {
      host.removeEventListener('pointerdown', down, true);
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      host.removeEventListener('dblclick', dbl, true);
    };
  }, [xapi]);

  const pts = draft.pts.map((q) => vp(q).join(',')).join(' ');
  const tail = !draft.naming && !draft.dragging && draft.pts.length && hover ? [vp(draft.pts[draft.pts.length - 1]), vp(hover)] : null;
  return (
    <div className="xb-measure" data-testid="zone-overlay">
      <div className="xb-cal-hint" data-testid="zone-banner" data-step={draft.naming ? 'name' : draft.pts.length ? 'drawing' : 'start'} data-blocked={blocked || undefined}>
        {blocked ? (
          <strong className="xb-zone-blocked" data-testid="zone-blocked">
            {zoneBlocked(draft)}
          </strong>
        ) : null}
        {draft.naming ? (
          <form
            className="xb-zone-name"
            onSubmit={(e) => {
              e.preventDefault();
              p.onCommit(draft.pts, name.trim() || draft.defaultName);
            }}
          >
            <span>Zone name</span>
            <input
              ref={nameRef}
              className="xb-input"
              value={name}
              onChange={(e) => setName(e.target.value.toUpperCase())}
              aria-label="Zone name"
              data-testid="zone-name"
              autoFocus
            />
            <button type="submit" className="xb-btn" data-testid="zone-save">
              Add zone
            </button>
            <span className="xb-mt-note">{zoneBanner(draft)}</span>
          </form>
        ) : (
          <span>{zoneBanner(draft)}</span>
        )}
      </div>
      <svg className="xb-cal-svg xb-zone-svg" width="100%" height="100%">
        {(p.ghosts ?? []).map((g, i) => (
          <polygon key={`g${i}`} className="xb-zone-ghost" points={g.map((q) => vp(q as Pt).join(',')).join(' ')} data-testid="zone-ghost" />
        ))}
        {draft.pts.length >= 2 ? (draft.naming || draft.dragging ? <polygon className="xb-zone-draft" points={pts} /> : <polyline className="xb-zone-draft" points={pts} />) : null}
        {tail ? <line className="xb-zone-tail" x1={tail[0][0]} y1={tail[0][1]} x2={tail[1][0]} y2={tail[1][1]} /> : null}
        {!draft.dragging
          ? draft.pts.map((q, i) => {
              const [x, y] = vp(q);
              return <circle key={i} cx={x} cy={y} r={i === 0 ? 5 : 3.5} />;
            })
          : null}
      </svg>
    </div>
  );
}
